/**
 * Interactive Demo Component - Product Tour Modal Content
 * Epic 18: High-Performance Marketing Site
 */

import React, { useState } from 'react';
import {
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel,
  Button,
  ProgressIndicator,
  ProgressStep,
  InlineNotification,
  Tile,
  Theme,
} from '@carbon/react';
import { PlayFilledAlt, PauseFilled, Restart } from '@carbon/icons-react';
import styles from './InteractiveDemo.module.scss';

interface InteractiveDemoProps {
  variant: 'main' | 'institution' | 'parent';
}

const InteractiveDemo: React.FC<InteractiveDemoProps> = ({ variant }) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);

  const demoSteps = {
    main: [
      {
        title: 'AI-Powered Lesson Planning',
        content: 'Create personalized lesson plans in minutes with our AI assistant.',
        duration: 5000,
      },
      {
        title: 'Automated Grading',
        content: 'Save hours with intelligent grading that provides detailed feedback.',
        duration: 5000,
      },
      {
        title: 'Student Progress Tracking',
        content: 'Monitor individual and class progress with real-time analytics.',
        duration: 5000,
      },
      {
        title: 'Parent Communication',
        content: 'Keep parents informed with automated progress reports.',
        duration: 5000,
      },
    ],
    institution: [
      {
        title: 'District-Wide Analytics',
        content: 'Track performance across all schools in your district.',
        duration: 5000,
      },
      {
        title: 'Curriculum Alignment',
        content: 'Ensure all teachers follow standardized curriculum.',
        duration: 5000,
      },
      {
        title: 'Resource Optimization',
        content: 'Allocate resources based on real-time needs.',
        duration: 5000,
      },
      {
        title: 'Compliance Reporting',
        content: 'Generate reports for state and federal requirements.',
        duration: 5000,
      },
    ],
    parent: [
      {
        title: 'Daily Progress Updates',
        content: 'See what your child learned today.',
        duration: 5000,
      },
      {
        title: 'Homework Help',
        content: 'AI tutor available 24/7 for homework assistance.',
        duration: 5000,
      },
      {
        title: 'Teacher Communication',
        content: 'Message teachers directly through the platform.',
        duration: 5000,
      },
      {
        title: 'Learning Recommendations',
        content: 'Get personalized recommendations for your child.',
        duration: 5000,
      },
    ],
  };

  const steps = demoSteps[variant];

  React.useEffect(() => {
    if (isPlaying && currentStep < steps.length - 1) {
      const timer = setTimeout(() => {
        setCurrentStep(currentStep + 1);
      }, steps[currentStep].duration);
      return () => clearTimeout(timer);
    } else if (isPlaying && currentStep === steps.length - 1) {
      setIsPlaying(false);
    }
  }, [isPlaying, currentStep, steps]);

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleRestart = () => {
    setCurrentStep(0);
    setIsPlaying(true);
  };

  return (
    <div className={styles.interactiveDemo}>
      <ProgressIndicator currentIndex={currentStep} spaceEqually>
        {steps.map((step, index) => (
          <ProgressStep
            key={index}
            label={step.title}
            description={step.content}
            onClick={() => setCurrentStep(index)}
          />
        ))}
      </ProgressIndicator>

      <div className={styles.demoContainer}>
        <Tile className={styles.demoTile}>
          <div className={styles.demoContent}>
            <h3 className={styles.demoTitle}>{steps[currentStep].title}</h3>
            <p className={styles.demoDescription}>{steps[currentStep].content}</p>
          </div>
        </Tile>
      </div>

      <div className={styles.buttonContainer}>
        <Button kind="primary" size="sm" renderIcon={isPlaying ? PauseFilled : PlayFilledAlt} onClick={handlePlayPause}>
          {isPlaying ? 'Pause' : 'Play'}
        </Button>
        <Button kind="secondary" size="sm" renderIcon={Restart} onClick={handleRestart}>
          Restart
        </Button>
      </div>

      <div className={styles.notificationContainer}>
        <InlineNotification
          kind="info"
          title="Interactive Demo"
          subtitle="Click through the steps or press play to watch the demo"
          hideCloseButton
          lowContrast
        />
      </div>
    </div>
  );
};

export default InteractiveDemo;
